import moment from 'moment';
import React, { useState } from 'react';
import { Select } from '@gerdau/hefesto/Select';
import { TSelectOptionProps } from '@gerdau/hefesto/Select/Select.types';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { ICustomer } from '../../store/ducks/customers/types';
import {
  EFabricationContractsStatus,
  IFabricationContract
} from '../../store/ducks/fabrication-contracts/types';
import { uppercaseOnlyFirstLetter } from '../../utils/formatStrings';
import { Container } from './styles';

interface IFabricationContractsSelectProps {
  onChange(value: string[]): void;
  contracts: IFabricationContract[];
  selectedContractNumbers: string[];
  loading: boolean;
  hideLabel?: boolean;
  placeholder?: string;
  title?: string;
  showClosedContractMessage?: boolean;
}

const FabricationMultipleContractsSelect: React.FC<
  IFabricationContractsSelectProps
> = ({
  selectedContractNumbers,
  onChange,
  contracts,
  hideLabel = false,
  loading,
  showClosedContractMessage = false
}) => {
  const customersState = useSelector(
    (appState: ApplicationState) => appState.customers
  );

  const getSoldToCustomer = (
    contract: IFabricationContract
  ): ICustomer | undefined => {
    return customersState.customers.find(
      (cust) => cust.customerId === contract.soldTo.code
    );
  };

  const getContracts = (): TSelectOptionProps[] => {
    const contractsOptions: TSelectOptionProps[] = [
      {
        value: 'all',
        label: 'Selecionar todos'
      }
    ];

    contracts.forEach((contract) => {
      const customer = getSoldToCustomer(contract);

      contractsOptions.push({
        value: contract.contractNumber,
        label: `${contract.contractNumber} ${
          contract.customerOrder ? ` - ${contract.customerOrder}` : ''
        }`,
        descriptions: [
          contract.creationDate
            ? moment(contract.creationDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
            : '',
          customer
            ? `${uppercaseOnlyFirstLetter(customer.name)} - ${
                customer.customerId
              }`
            : '',
          showClosedContractMessage &&
          contract.status === EFabricationContractsStatus.CLOSED
            ? 'Este contrato já foi encerrado'
            : ''
        ]
      });
    });

    return contractsOptions;
  };

  const [isSelectedAll, setIsSelectedAll] = useState<boolean>(false);
  const contractsValues = contracts.map((state) => state.contractNumber);

  return (
    <Container>
      <Select
        data-testid="select-fabrication-construction-identification"
        name="contractsSelect"
        label="Contrato da obra"
        hideLabel={hideLabel}
        value={
          isSelectedAll
            ? [...selectedContractNumbers, 'all']
            : selectedContractNumbers
        }
        placeholder={`- Selecione -`}
        onSelect={(value: string | string[]) => {
          const onlySelectedContracts = (value as string[]).filter(
            (selectedState) => selectedState !== 'all'
          );
          const isAllSelectedContracts =
            onlySelectedContracts.length === contracts.length;

          if (
            !(value as string[]).includes('all') &&
            isAllSelectedContracts &&
            isSelectedAll
          ) {
            setIsSelectedAll(false);
            return onChange([]);
          }

          if (isAllSelectedContracts) {
            setIsSelectedAll(true);
            return onChange([...contractsValues]);
          }

          if ((value as string[]).includes('all') && !isSelectedAll) {
            setIsSelectedAll(true);
            return onChange([...contractsValues]);
          }

          setIsSelectedAll(false);
          return onChange(onlySelectedContracts);
        }}
        options={getContracts()}
        disabled={loading}
        isMultipleSelect
      ></Select>
      {contracts.length === 0 && !loading && (
        <span className="no-contracts">
          Não foram encontrados contratos disponíveis.
        </span>
      )}
    </Container>
  );
};

export default FabricationMultipleContractsSelect;
